import graphQLClient from '../../../helpers/graphQLClient';
import { STRIPE_CHECKOUT_LINK } from '../../../helpers/graphql/stripe';
import Button from '@mui/material/Button';
import PlanPickerOption from './PlanPickerOption';
import { useState } from 'react';
import FormGroup from '@mui/material/FormGroup';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { useIsIosApp } from '../../../helpers/appPlatform';
import NotAvailableInApp from '../../General/NotAvailableInApp';
import { useSnackbar } from 'notistack';

export default function PlanPicker({ blogId }) {
  const [selected, setSelected] = useState('pro');
  const [isMonthly, setIsMonthly] = useState(false);
  const [checkoutClicked, setCheckoutClicked] = useState(false);
  const inIosApp = useIsIosApp();
  const { enqueueSnackbar } = useSnackbar();

  const getCheckoutLink = () => {
    setCheckoutClicked(true);
    graphQLClient(STRIPE_CHECKOUT_LINK, {
      plan: selected,
      isMonthly,
      blogId,
    })
      .then(res => {
        //   Redirect to checkout link
        if (res && res.stripeCheckoutLink) {
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          if (isIOS) {
            window.open(res.stripeCheckoutLink, '_blank');
          } else {
            window.location.href = res.stripeCheckoutLink;
          }
        } else {
          // The backend answers '' on every graceful failure — a spinner
          // that just stops looks like a dead button.
          enqueueSnackbar(
            'Could not start the checkout. Please try again or contact us.',
            { variant: 'error' },
          );
        }
        setCheckoutClicked(false);
      })
      .catch(err => {
        console.error(err);
        enqueueSnackbar(
          'Could not start the checkout. Please try again or contact us.',
          { variant: 'error' },
        );
        setCheckoutClicked(false);
      });
  };

  const plans = [
    {
      title: 'basic',
      pricing: { monthly: 19, yearly: 15 },
      features: 'Your own domain • Custom design • Comments',
    },
    {
      title: 'pro',
      pricing: { monthly: 29, yearly: 25 },
      features: 'Everything in Basic + Search, world map & contact form • More AI credits',
      isHighlighted: true,
    },
    {
      title: 'vip',
      pricing: { monthly: 49, yearly: 45 },
      features: 'Everything in Pro + the most AI credits • Dedicated support',
    },
  ];

  if (inIosApp) {
    return (
      <div className="tw-max-w-md tw-mx-auto">
        <NotAvailableInApp title="Plan purchases are not available in this app" />
      </div>
    );
  }

  return (
    <>
      <div className="tw-mx-auto">
        <Typography
          component="h2"
          variant="h4"
          gutterBottom
          align="center"
          className="tw-font-semibold"
        >
          Choose your Plan
        </Typography>
        <FormGroup>
          <Typography component="div" className="tw-m-auto">
            <Grid
              component="label"
              container
              alignItems="center"
              spacing={1}
              className="tw-cursor-pointer"
            >
              <Grid item>Monthly</Grid>
              <Grid item>
                <Switch
                  checked={!isMonthly}
                  onChange={() => setIsMonthly(!isMonthly)}
                  name="checkedA"
                  color="primary"
                />
              </Grid>
              <Grid item>Yearly (save up to 21%)</Grid>
            </Grid>
          </Typography>
        </FormGroup>

        <div className="tw-mt-6 tw-space-y-8 xl:tw-mt-12">
          {plans.map(plan => (
            <PlanPickerOption
              key={plan.title}
              plan={plan}
              setSelected={setSelected}
              selected={selected}
              isMonthly={isMonthly}
            />
          ))}

          <div className="tw-flex tw-justify-center">
            <Button
              color="primary"
              variant="contained"
              onClick={getCheckoutLink}
              disabled={checkoutClicked}
              size="large"
            >
              Choose Plan
            </Button>
          </div>
        </div>
        <div className="text-center postcontent">
          <Typography
            component="p"
            variant="body2"
            gutterBottom
            className="tw-mt-5"
          >
            Undecided?{' '}
            <Link href="/join#pricing">View detailed comparison</Link>
          </Typography>
        </div>
      </div>
    </>
  );
}
